/**
 * Roles and what they may do.
 *
 * The wire values are `SUPER_ADMIN | SUPPORT | ORG_ADMIN | ORG_USER | GESTOR_INDIVIDUAL | ORG_VIEWER`
 * (architecture spec §13.2). The mockups label them in Spanish — Administrador ·
 * Gestor de tráfico · Gestor Individual · Solo lectura — so the mapping is explicit here and the
 * display strings live in the i18n catalogues, never inline.
 *
 * This is a **UI affordance**, not the authorisation boundary. The server owns
 * that: it scopes every query by the principal's organisation and rejects an
 * out-of-role call with 403 regardless of what the portal renders.
 */

import type { CurrentPrincipal, Role } from '@deca/api'

/** Roles a company administrator may assign to its own sub-users — never SUPER_ADMIN. */
const CLIENT_ASSIGNABLE_ROLES: readonly Role[] = ['ORG_ADMIN', 'ORG_USER', 'GESTOR_INDIVIDUAL', 'ORG_VIEWER'] as const

/**
 * The roles worth creating on the platform organisation — ASGTRANS's own two, and nothing else.
 *
 * ASGTRANS's tenant owns no documents, so an `ORG_ADMIN`, `ORG_USER` or `ORG_VIEWER` created there
 * lands in exactly the empty Panel and empty DeCA list that #14 is about — this branch would be
 * removing that portal for the first ASGTRANS employee and offering it to the second. The platform
 * roles are therefore not merely *added* to the list a platform admin sees; they are the list.
 *
 * ⚠️ `SUPPORT` belongs here for the same reason `SUPER_ADMIN` does, and was missing only because
 * this list predates the role. While it was, the backend accepted the role and the portal offered
 * it nowhere, so provisioning a support agent meant a hand-written API call with a super-admin
 * token. Ordered as the contract's `Role` enum orders them.
 */
const PLATFORM_ASSIGNABLE_ROLES: readonly Role[] = ['SUPER_ADMIN', 'SUPPORT'] as const

/**
 * The tenant ladder. **Platform roles do not sit on it** — `SUPER_ADMIN` is already outside it for
 * documents (see `abilitiesFor`), and `SUPPORT` administers nothing at all, so ranking it above
 * `ORG_ADMIN` would hand it `administer` through arithmetic. Both are placed where they can only
 * lose a comparison; what they may actually do is decided explicitly below.
 */
const RANK: Record<Role, number> = {
  SUPPORT: 0,
  ORG_VIEWER: 0,
  ORG_USER: 1,
  // Writes as ORG_USER does; what it may reach is the server's owner rule, not a rank.
  GESTOR_INDIVIDUAL: 1,
  ORG_ADMIN: 2,
  SUPER_ADMIN: 3,
}

export function atLeast(role: Role | null | undefined, minimum: Role): boolean {
  if (!role) return false
  return RANK[role] >= RANK[minimum]
}

export interface Abilities {
  /**
   * Whether this role may look at a document at all — the list and the detail screen.
   *
   * True for every signed-in role, platform ones included, and kept **separate from
   * `handlesDecas`** rather than being it widened. `writeDecas` and `voidDecas` are derived from
   * `handlesDecas`, so granting reading through that flag would hand the platform roles create,
   * group, import and void by arithmetic — the same way ranking `SUPPORT` on the tenant ladder
   * would have handed it `administer`. Paco was explicit that creation stays with the client
   * companies.
   */
  readsDecas: boolean
  /**
   * Whether this role deals with DeCA documents at all — the Panel, the list, the
   * create and group screens, the detail screen and the import.
   *
   * False for `SUPER_ADMIN`, which is not a rank thing: ASGTRANS administers client
   * companies and does not work inside one (design 2026-08-06 §2), so its portal has
   * no document surface rather than an empty one. Every other role has it.
   */
  handlesDecas: boolean
  /** Create, generate, modify, group and import DeCAs. */
  writeDecas: boolean
  /** Void a document — separated from `writeDecas` because it is destructive. */
  voidDecas: boolean
  /** Manage the organisation's sub-users and API keys, and read the audit trail. */
  administer: boolean
  /** Manage client companies. ASGTRANS only. */
  platformAdmin: boolean
  /**
   * Whether this role may pick a client company to act inside — the organisation selector.
   *
   * Not `platformAdmin`, which is about *managing* companies and is `SUPER_ADMIN` only. `SUPPORT`
   * manages nothing and still needs the context: opening one company's document is an act inside
   * that company, and the server resolves it from `X-Organisation-Id`.
   */
  choosesOrganisation: boolean
  /**
   * The *Empresa* tab: the company's own driver permissions and its linked phones (#110). A company
   * administrator only — `administer` alone would add `SUPER_ADMIN`, which works for no company
   * and sets the two permissions in *Editar empresa* instead.
   */
  managesCompany: boolean
  /**
   * The documents this user reaches are only their own — the Gestor Individual (Addendum No. 3,
   * Point 7). Wording only: the server decides what they reach, and an empty list says their
   * documents will appear there rather than that the company has none.
   */
  ownDocumentsOnly: boolean
}

export function abilitiesFor(principal: CurrentPrincipal | null | undefined): Abilities {
  const role = principal?.role ?? null
  // The platform role is outside the ranking for documents. `atLeast` alone would give
  // SUPER_ADMIN every document ability, because it outranks ORG_USER — true of the
  // ranking, false of the job.
  // ⚠️ Both platform roles are excluded, not just SUPER_ADMIN. They read every company's documents
  // through `readsDecas` below; this flag is the **tenant** surface, whose create, group and import
  // affordances belong to a company that owns documents, and whose Panel shows a haulier's own
  // month. An ASGTRANS agent dropped on that Panel would see an empty one, which is #14 again.
  const handlesDecas = role !== null && role !== 'SUPER_ADMIN' && role !== 'SUPPORT'
  const administer = atLeast(role, 'ORG_ADMIN')
  return {
    readsDecas: role !== null,
    handlesDecas,
    writeDecas: handlesDecas && atLeast(role, 'ORG_USER'),
    // A Gestor Individual cancels their own DeCAs (Point 7): rank cannot say it, since the role
    // ranks with ORG_USER, who may not.
    voidDecas: handlesDecas && (atLeast(role, 'ORG_ADMIN') || role === 'GESTOR_INDIVIDUAL'),
    administer,
    platformAdmin: role === 'SUPER_ADMIN',
    choosesOrganisation: role === 'SUPER_ADMIN' || role === 'SUPPORT',
    managesCompany: administer && handlesDecas,
    ownDocumentsOnly: role === 'GESTOR_INDIVIDUAL',
  }
}

/**
 * Which roles the create-user form may offer.
 *
 * The backend decides this twice, and the portal has to agree with both halves:
 * `assertRoleAssignable` (`backend/src/http/routes/admin.ts:382`) accepts `SUPER_ADMIN` only from
 * a platform super-admin **and only on the platform organisation**, and `repositories.createUser`
 * refuses to write one anywhere else at all.
 *
 * So `platformAdmin` is not the whole question — *which company the user is being created in* is
 * the other half, and since the organisation selector it is no longer always ASGTRANS's own. A
 * platform admin acting inside a client company creates that company's users, and offering
 * `SUPER_ADMIN` there would leave them a form whose only option the server answers 403 to. Acting
 * inside no company, it is ASGTRANS's own staff being created, and `SUPER_ADMIN` is then not
 * merely on the list — it is the list (see `PLATFORM_ASSIGNABLE_ROLES`).
 *
 * `actingInsideAClientCompany` is **required rather than defaulted**: a default is how a new call
 * site silently gets the old answer, and here the old answer is a form that cannot submit.
 */
export function assignableRoles(
  abilities: Abilities,
  actingInsideAClientCompany: boolean,
): readonly Role[] {
  return abilities.platformAdmin && !actingInsideAClientCompany
    ? PLATFORM_ASSIGNABLE_ROLES
    : CLIENT_ASSIGNABLE_ROLES
}

export function roleKey(role: Role): string {
  return `role.${role}`
}

export function initialsOf(name: string | null | undefined, fallback = '—'): string {
  if (!name) return fallback
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return fallback
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}
